import {Component, OnDestroy, OnInit} from '@angular/core';
import {SocialAuthService, SocialUser} from "@abacritt/angularx-social-login";
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import googleData from '../googleCodes.json'
import bingData from '../msCodes.json'
import {lastValueFrom, Subject, timer} from "rxjs";
import {WindowRefService} from "../../../helpers/windowref.service";
import moment from 'moment-timezone';
import {ApiCallService} from "../../../helpers/api-call.service";
import {Timezone} from "../../../helpers/models";

@Component({
    selector: 'app-create-google-ads-account',
    templateUrl: './create-google-ads-account.component.html',
    styleUrls: ['./create-google-ads-account.component.scss'],
    standalone: false
})

export class CreateGoogleAdsAccountComponent implements OnInit, OnDestroy {
  componentDestroyed: Subject<boolean> = new Subject();
  user: SocialUser | undefined;
  channel: string = 'google';
  page: string | undefined;
  currencies: object[] = [];
  timezones: object[] = [];
  accountName: string | null = null;
  selectedCurrency: string | null = null;
  selectedTimezone: string | null = null;
  loading: boolean = false;
  brandNames = '';
  website = '';
  businessType: string | null = null;
  eu: string | null = null;
  businessTypes = [
    {label: 'E-commerce', value: 'e-commerce'},
    {label: 'Other', value: 'other'}
  ];
  eus = [
    {label: 'EU countries', value: 'eu'},
    {label: 'Non-EU countries', value: 'non-eu'}
  ];
  websiteError: string | null = null;
  verified = false;
  sendVerifyError = false;
  codeError = false;
  inProcess = false;
  resendTimes = 0;
  countdown = 30;
  isResendDisabled = false;
  code: string | null = null;
  taskId: string | null = null;
  showResentResult = false;
  resentResult = 'An error occur when resending the verification code!';

  constructor(private authService: SocialAuthService, public ref: DynamicDialogRef,
              private apiCallService: ApiCallService,
              public dialogConfig: DynamicDialogConfig, private winRef: WindowRefService) { }

  async ngOnInit(): Promise<void> {
    this.user = this.dialogConfig?.data.user;
    const params = {
      identifier: this.user?.email,
      channel: 'email'
    }
    const startVerifyCode$ = this.apiCallService.startVerifyCode(params);
    const response = await lastValueFrom(startVerifyCode$);
    if (response !== 'success') {
      this.sendVerifyError = true;
    } else {
      this.startCountdown(30);
    }
    this.channel = this.dialogConfig?.data.channel;
    if (this.dialogConfig?.data.page) {
      this.page = this.dialogConfig?.data.page;
    } else {
      this.page = this.channel;
    }
    const currencyCodes = this.channel === 'google' ? googleData.currencyCodes: bingData.currencyCodes;
    this.currencies = Array.from(currencyCodes, x => ({label: x, value: x}));
    this.selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const gmtValue = moment().tz(this.selectedTimezone).format('ZZ');
    if (this.channel === 'google') {
      this.timezones = Array.from(googleData.timezones, x => ({label: x + ' GMT' + moment().tz(x).format('ZZ'), value: x}));
    } else {
      this.timezones = Array.from(bingData.timezones) ;
      console.log(gmtValue);
      const formatedTimezones = this.timezones as Timezone[];
      const matchingTimezones = formatedTimezones.filter(item => item.label.includes(gmtValue));
      const geoInfo = this.getGeoInfo();
      const countrySpecificMatch = matchingTimezones.find(item =>
        geoInfo.some(geo => item.label.includes(geo))
      );
      this.selectedTimezone = countrySpecificMatch
        ? countrySpecificMatch.value
        : matchingTimezones.length > 0
          ? matchingTimezones[0].value
          : null;
    }
  }

  private getGeoInfo(): any[] {
    const nativeGeoIp = this.winRef.nativeGeoIp;
    let geoInfo: any[] = [];
    if (nativeGeoIp) {
      nativeGeoIp.city(
        (geoipResponse: any) => {geoInfo = [geoipResponse.country.iso_code, geoipResponse.country.names.en,
          geoipResponse.city.names.en, geoipResponse.subdivisions.names.en];},
        (err: any) => {console.log('nativeGeoIp Error', err);});
    }
    return geoInfo;
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next(true);
    this.componentDestroyed.complete();
  }

  get channelMsg(): string {
    return this.channel === 'google'? 'Google': 'Microsoft';
  }

  async createAccount(): Promise<void> {
    // For UI testing purposes
    // this.loading = true;
    // const response = {'result': 'created', 'customerId': '1234567890'}
    // const returnData = {response: response, user: this.user};
    // sessionStorage.setItem(<string>this.user?.email, JSON.stringify(response));
    // this.loading = false;
    // this.ref.close(returnData);

    // For production
    this.websiteError = null;
    if (this.website && this.website.length) {
      const re2 = new RegExp('^(http(s)?://)[\\w.-]+(?:\\.[\\w\\.-]+)+[\\w\\-._~:/?#[\\]@!$&\'()*+,;=]+$');
      if (!re2.test(this.website || '')) {
        this.websiteError = 'Please enter a valid website address';
      } else {
        this.loading = true;
        const recaptchaToken = await this.apiCallService.executeRecaptcha('create_account' + this.page);
        const params = {
          user: this.user,
          accountName: this.accountName,
          currency: this.selectedCurrency,
          timezone: this.selectedTimezone,
          geo: this.dialogConfig?.data.geo,
          sessionToken: sessionStorage.getItem(this.page + 'Token'),
          brandNames: this.brandNames,
          hasShoppingAds: this.businessType === 'e-commerce',
          website: this.website,
          hasGdprRegions: this.eu === 'eu',
          channel: this.channel,
          page: this.page,
          code: this.code,
          taskId: this.taskId,
          recaptchaToken
        };
        const createAccount$ = this.apiCallService.createAccount(params, window.location.search);
        const response = await lastValueFrom(createAccount$);
        // console.log('createAccount: ', {response});
        this.winRef.reportGtagEventNew('createNewAccount');
        this.loading = false;
        this.ref.close(response);
      }
    }
  }

  cancel(): void {
    this.ref.close();
  }

  removeError(): void {
    this.websiteError = null;
  }


  get disabled(): boolean {
    return !this.accountName?.length ||  !this.selectedCurrency?.length || !this.selectedTimezone?.length ||
      !this.brandNames?.length || !this.website?.length || this.businessType == null || this.eu == null ||
      this.websiteError !== null || this.loading;
  }

  onCodeChange(): void {
    this.codeError = false;
    this.showResentResult = false;
  }

  async submit(){
    if (this.code?.length !== 6){
      this.codeError = true;
    } else {
      this.inProcess = true;
      const params = {
        identifier: this.user?.email,
        code: this.code
      }
      const createVerifyCode$ = await this.apiCallService.createVerifyCode(params);
      const response = await lastValueFrom(createVerifyCode$);
      if (response.status === 'approved') {
        this.verified = true;
        this.taskId = response.taskId;
      } else {
        this.codeError = true;
      }
      this.inProcess = false;
    }
  }

  async send2faRequest() {
    this.inProcess = true;
    this.codeError = false;
    const params = {
      identifier: this.user?.email,
      channel: 'email'
    }
    const sendCode$ = this.apiCallService.startVerifyCode(params);
    const response = await lastValueFrom(sendCode$);
    if (response === 'success') {
      console.log('Resend success.')
      this.resentResult = 'A code has been resent to your email!';
      this.showResentResult = true;
      // this.messageService.add({
      //   key: '2faMessage',
      //   severity: 'success',
      //   closable: false,
      //   detail: 'A code has been resent to your email!'});
      this.resendTimes++;
    } else {
      console.log('Resend error.')
      this.resentResult = 'An error occur when resending the verification code!';
      this.showResentResult = true;
      // this.messageService.add({
      //   key: '2faMessage',
      //   severity: 'error',
      //   closable: false,
      //   detail: 'An error occur when resending the verification code!'});
    }
    this.inProcess = false;
    this.startCountdown(30);
  }

  startCountdown(seconds: number): void {
    this.isResendDisabled = true;
    this.countdown = seconds;
    const countdownTimer = timer(0, 1000).subscribe(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        this.isResendDisabled = false;
        countdownTimer.unsubscribe(); // Stop the timer
      }
    });
  }
}
